import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AnalysisResult } from '../types';
import { AnalysisService } from '../services';
import { ResultsSection, LoadingOverlay } from './';

export const AnalysisView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if results were passed via navigation state
  const stateResults = location.state?.results as AnalysisResult | undefined;
  
  const [results, setResults] = useState<AnalysisResult | null>(stateResults || null);
  const [loading, setLoading] = useState<boolean>(!stateResults && !!id);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadAnalysis = async () => {
      if (!id || stateResults) {
        return; // Don't load if we already have results or no ID
      }

      try {
        setLoading(true);
        setError('');
        const analysis = await AnalysisService.getAnalysis(id);
        setResults(analysis);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };

    loadAnalysis();
  }, [id, stateResults]);

  const handleReset = () => {
    navigate('/');
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
          Error Loading Analysis
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {error}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-600 dark:text-gray-400 mb-4">
          No Analysis Found
        </h1>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <ResultsSection
      results={results}
      onReset={handleReset}
      showPermalink={!!id}
    />
  );
};